// ─────────────────────────────────────────────────────────────────────────────
// REVEAL AN INLINE FORM WHERE THE PERSON IS LOOKING (Carla, proven live 2026-10-21)
//
// Emails -> Reply rendered its form ~1263px down a 734px window with no scroll and
// no focus, so Reply looked dead. The rule for EVERY action that reveals a form,
// composer or editor inline: bring it into view and put the cursor in it.
//
// revealForm(el): scrolls `el` into view (block 'nearest' when it fits, so a form
// already on screen does not move; otherwise its field is centred), smooth unless
// the person asked for reduced motion, then focuses the field WITHOUT scrolling again.
// The field is the one marked data-reveal-focus, else the first focusable field.
//
// useRevealForm(open): returns a ref for the form's container; when `open` turns
// true (or changes to another truthy id) the form is revealed once, not on every render.
//
// <Reveal>: a container that reveals itself when it mounts, for a form rendered by a toggle
// (`{open && <Reveal className="x-form">…</Reveal>}`). `as` picks the element (div by default).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef } from 'react';

const FIELD = [
  'input:not([type="hidden"]):not([disabled]):not([readonly])',
  'textarea:not([disabled]):not([readonly])',
  'select:not([disabled])',
  '[contenteditable="true"]',
  '[contenteditable=""]',
].join(', ');

// Room kept clear above and below for the sticky top bar, the phone's bottom bar and the
// floating Aidey button: scroll-margin is honoured by scrollIntoView.
const MARGIN_PX = 88;

export function firstField(el) {
  if (!el || !el.querySelector) return null;
  if (el.matches && el.matches('[data-reveal-focus]')) return el;
  const marked = el.querySelector('[data-reveal-focus]');
  if (marked) return marked;
  if (el.matches && el.matches(FIELD)) return el;
  return el.querySelector(FIELD);
}

function prefersReducedMotion() {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
}

export function revealForm(el, { focus = true } = {}) {
  if (!el) return null;
  const field = focus ? firstField(el) : null;
  const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
  const viewH = (typeof window !== 'undefined' && window.innerHeight) || 0;
  const rect = el.getBoundingClientRect ? el.getBoundingClientRect() : { height: 0 };
  const fits = !viewH || rect.height <= viewH - 2 * MARGIN_PX;
  const target = fits || !field ? el : field;
  try {
    target.style.scrollMarginTop = `${MARGIN_PX}px`;
    target.style.scrollMarginBottom = `${MARGIN_PX}px`;
  } catch { /* a read-only style is not worth failing over */ }
  if (typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ block: fits ? 'nearest' : 'center', inline: 'nearest', behavior });
  }
  if (field && typeof field.focus === 'function') {
    try { field.focus({ preventScroll: true }); } catch { field.focus(); }
  }
  return field;
}

export function useRevealForm(open, opts) {
  const ref = useRef(null);
  const was = useRef(null);
  useEffect(() => {
    // `open` may be a boolean or WHICH thing is open (an id): a change to another one reveals again.
    if (open && open !== was.current && ref.current) revealForm(ref.current, opts);
    was.current = open || null;
  // opts is read once at reveal time; changing it must not re-reveal
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return ref;
}

export const Reveal = React.forwardRef(function Reveal({ as: Tag = 'div', focus = true, children, ...rest }, outer) {
  const ref = useRevealForm(true, { focus });
  const setRef = (el) => {
    ref.current = el;
    if (typeof outer === 'function') outer(el); else if (outer) outer.current = el;
  };
  return <Tag ref={setRef} {...rest}>{children}</Tag>;
});

// useViewTop(key): for an editor that REPLACES the page (a contract builder over the list), the
// window keeps the list's scroll, so the editor opened part-way down. When `key` changes to a
// truthy value (which view or item is open), the window goes back to the top, at once.
export function useViewTop(key) {
  const was = useRef(null);
  useEffect(() => {
    if (key && key !== was.current && typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch { window.scrollTo(0, 0); }
    }
    was.current = key || null;
  }, [key]);
}
